import React, { useEffect, useState } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

// Actions
import { SG_GET_CULTURE_AUDIT_REPORTS } from "constants/actions";

// Components
import DebriefSelector from "./DebriefSelector";

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 24px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 16px;
  color: #6c757d;
  margin: 0;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: ${props => props.selected ? '#e8f5e8' : '#f5f5f5'};
  color: ${props => props.selected ? '#2d5016' : '#666'};
  font-size: 12px;
  font-weight: 500;
`;

const PulseSummaryCard = styled.div`
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid #007bff;
`;

const SummaryTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
`;

const SummaryDetails = styled.div`
  font-size: 14px;
  color: #6c757d;
  line-height: 1.4;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e0e0e0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Button = styled.button`
  background: ${props => props.primary ? '#007bff' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    background: ${props => props.disabled ? (props.primary ? '#007bff' : '#6c757d') : (props.primary ? '#0056b3' : '#5a6268')};
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #f5c6cb;
  text-align: center;
`;

const SurveySelectionStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [pulseSurveyData, setPulseSurveyData] = useState(null);
  const [selectedPulseSurvey, setSelectedPulseSurvey] = useState(null);

  // Redux selectors
  const {
    get_culture_audit_reports,
    get_auth,
    loading
  } = useSelector(
    (state) => ({
      get_culture_audit_reports: state.debrief_schedule.culture_audit_reports,
      get_auth: state.auth,
      loading: state.debrief_schedule.loading
    }),
    shallowEqual
  );

  // Extract data from navigation state
  useEffect(() => {
    if (location.state) {
      setPulseSurveyData(location.state.pulseSurveyData);
      setSelectedPulseSurvey(location.state.selectedPulseSurvey);
    }
  }, [location.state]);

  // Load audit reports on component mount
  useEffect(() => {
    if (get_auth?.organization_id) {
      dispatch({
        type: SG_GET_CULTURE_AUDIT_REPORTS,
        payload: `audit-type-id=5&organization=${get_auth?.organization_id}`,
      });
    }
  }, [dispatch, get_auth?.organization_id]);

  // Handle survey selection
  const handleSurveySelect = (survey, data) => {
    setSelectedSurvey(survey);
    setSurveyData(data);
  };

  // Handle going back to step 1
  const handleBackToStep1 = () => {
    navigate("/app/otp/otp-comparison");
  };

  // Handle starting analysis
  const handleStartAnalysis = () => {
    if (selectedSurvey && surveyData && pulseSurveyData) {
      navigate("/app/otp/otp-comparison/analysis", {
        state: {
          pulseSurveyData: pulseSurveyData,
          debriefData: surveyData,
          selectedPulseSurvey: selectedPulseSurvey,
          selectedDebrief: selectedSurvey
        }
      });
    }
  };

  // If no pulse survey data, redirect back to step 1
  if (!pulseSurveyData || !selectedPulseSurvey) {
    return (
      <PageContainer>
        <Container>
          <ErrorMessage>
            No pulse survey data found. Please start from Step 1.
          </ErrorMessage>
          <ActionSection>
            <Button onClick={handleBackToStep1}>
              ← Back to Step 1
            </Button>
          </ActionSection>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Data Comparison Tool</PageTitle>
        <PageSubtitle>Step 2: Select a survey to compare with your pulse survey</PageSubtitle>
      </PageHeader>

      <Container>
        {/* Selected Pulse Survey Summary */}
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Selected Pulse Survey</SectionTitle>
            <StatusIndicator selected={true}>✓ Selected</StatusIndicator>
          </SectionHeader>
          
          <PulseSummaryCard>
            <SummaryTitle>{selectedPulseSurvey?.name || 'Pulse Survey'}</SummaryTitle>
            <SummaryDetails>
              <div><strong>Participants:</strong> {pulseSurveyData?.participants?.length || 0}</div>
              <div><strong>Responses:</strong> {pulseSurveyData?.responses?.length || 0}</div>
              <div><strong>Compliance:</strong> {pulseSurveyData?.compliance?.percentage || 0}%</div>
              <div><strong>Questions:</strong> {pulseSurveyData?.questions?.length || 0}</div>
            </SummaryDetails>
          </PulseSummaryCard>
        </SectionCard>

        {/* Survey Selection */}
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Select Survey for Comparison</SectionTitle>
            <StatusIndicator selected={!!selectedSurvey}>
              {selectedSurvey ? '✓ Selected' : 'Not Selected'}
            </StatusIndicator>
          </SectionHeader>
          
          <DebriefSelector
            auditReports={get_culture_audit_reports}
            onSelect={handleSurveySelect}
            selectedDebrief={selectedSurvey}
            loading={loading}
          />
        </SectionCard>

        {/* Action Buttons */}
        <ActionSection>
          <Button onClick={handleBackToStep1}>
            ← Back to Step 1
          </Button>
          <Button
            primary
            onClick={handleStartAnalysis}
            disabled={!selectedSurvey}
          >
            {selectedSurvey ? 'Start Analysis' : 'Select Survey to Continue'}
          </Button>
        </ActionSection>
      </Container>
    </PageContainer>
  );
};

export default SurveySelectionStep; 